import { motion } from "framer-motion";
import { LOCATION } from "@/content";
import { Landmark, Plane, Building2, MapPin } from "lucide-react";

const ICONS = {
  landmark: Landmark,
  plane: Plane,
  hospital: Building2,
};

export default function Location() {
  return (
    <section
      id="location"
      data-testid="section-location"
      className="relative py-24 lg:py-36 bg-brand-olive-deep text-brand-cream overflow-hidden"
    >
      <div className="absolute inset-0 opacity-[0.04] bg-grid-soft" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <span
                className="block text-xs text-brand-accent mb-5"
                data-testid="location-kicker"
              >
                {LOCATION.kicker}
              </span>
              <h2
                className="text-4xl sm:text-5xl lg:text-6xl text-brand-cream mb-6 leading-tight font-sans"
                data-testid="location-headline"
              >
                {LOCATION.headline}
              </h2>
              <p className="text-base lg:text-lg text-brand-cream/75 leading-loose mb-10 max-w-lg">
                {LOCATION.description}
              </p>

              <ul className="space-y-5" data-testid="location-points">
                {LOCATION.points.map((p, i) => {
                  const Icon = ICONS[p.icon] || MapPin;
                  return (
                    <motion.li
                      key={i}
                      initial={{ opacity: 0, x: 30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: i * 0.15 }}
                      className="flex items-center gap-5 group"
                      data-testid={`location-point-${i}`}
                    >
                      <div className="flex items-center justify-center w-14 h-14 border border-brand-accent/40 rounded-full bg-brand-accent/10 group-hover:bg-brand-accent/20 transition-colors">
                        <Icon size={22} className="text-brand-accent" strokeWidth={1.4} />
                      </div>
                      <div className="flex-1 flex items-baseline justify-between border-b border-brand-cream/15 pb-3">
                        <span className="text-base lg:text-lg text-brand-cream/90">{p.label}</span>
                        <div className="flex items-baseline gap-2">
                          <span className="text-3xl lg:text-4xl text-brand-accent">
                            {p.time}
                          </span>
                          <span className="text-xs text-brand-cream/60">{LOCATION.unit}</span>
                        </div>
                      </div>
                    </motion.li>
                  );
                })}
              </ul>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="lg:col-span-7 relative aspect-[4/3] rounded-2xl overflow-hidden border border-brand-accent/30 shadow-2xl"
          >
            <iframe
              title="موقع مشروع تاون هاوس الشامخة على خرائط Google"
              src="https://www.google.com/maps?q=26.3562956,50.0565544&z=17&output=embed"
              className="w-full h-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
            <a
              href="https://maps.app.goo.gl/o2fizPBSQgwMAerv8"
              target="_blank"
              rel="noopener noreferrer"
              className="absolute bottom-5 end-5 bg-brand-cream text-brand-ink px-5 py-3 rounded-xl flex items-center gap-2 shadow-lg hover:bg-white transition-colors"
            >
              <MapPin size={16} className="text-brand-olive" />
              <span className="text-sm font-medium">الدمام — حديقة الأثير</span>
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
